import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [playback]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  // Track genuine mouse hover, never the sticky :hover state emulated after touch.
  #hovering = false;
  #focusSuspended = false;
  #explicitPlayback = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.addEventListener('change', this.#handleMotionChange);

    this.resume();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.removeEventListener('change', this.#handleMotionChange);
    this.#hovering = false;
    this.#focusSuspended = false;
    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * An explicit Play may resume motion requested by the customer. Automatic
   * lifecycle resumes never override reduced motion or a manual pause.
   */
  play() {
    if (!this.autoplay) return;
    this.paused = false;
    this.#explicitPlayback = true;
    this.#focusSuspended = false;
    this.resume();
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
    this.#syncPlaybackControl();
  }

  togglePlayback() {
    if (this.#playbackPaused) this.play();
    else this.pause();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    this.suspend();
    this.#syncPlaybackControl();
    if (!this.isConnected || !this.autoplay || this.#playbackPaused || this.#hovering || this.#focusSuspended || document.hidden) return;

    this.#interval = setInterval(() => {
      if (this.#playbackPaused || this.#hovering || this.#focusSuspended || document.hidden) return;
      this.next();
    }, this.autoplayInterval);
  }

  get #playbackPaused() {
    return this.paused || (this.#reducedMotion.matches && !this.#explicitPlayback);
  }

  #syncPlaybackControl() {
    const paused = this.#playbackPaused;
    this.toggleAttribute('data-playback-paused', paused);
    this.setAttribute('aria-live', this.autoplay && !paused ? 'off' : 'polite');
    const button = this.refs.playback;
    const label = paused ? button?.dataset.playLabel : button?.dataset.pauseLabel;
    if (button && label) button.setAttribute('aria-label', label);
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (!Number.isFinite(value) || value <= 0) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#hovering = true;
    this.suspend();
  };

  /** @param {PointerEvent} event */
  #handlePointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#hovering = false;
    this.resume();
  };

  #handleFocusIn = () => {
    this.#focusSuspended = true;
    this.suspend();
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#focusSuspended = false;
    this.resume();
  };

  #handleMotionChange = () => {
    this.#explicitPlayback = false;
    this.resume();
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
